import { createSelector } from '@reduxjs/toolkit';
import { Map as ImmutableMap, OrderedMap } from 'immutable';
import { DateTime } from 'luxon';

import { accountsSelectors } from 'app-components/flux/accounts';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { scheduledTransactionsSelectors } from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';

const txnIsPosted = (txn) => txn.source !== 'SCHEDULED_TRANSACTION_PENDING' && txn.state !== 'PENDING';

const transferCheck = (txn, model) => {
  if (txn.coa) {
    if (txn.coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
      // make sure only one side of the transfer displays based on matching the model
      return model && Math.sign(model.transaction?.amount) === Math.sign(txn.amount);
    }
    return true;
  }
  if (txn.split) {
    return true;
  }
  return false;
};

export const getPostedAndExpectedScheduledInstances = createSelector(
  (state) =>
    transactionsSelectors.getTransactionsOfTypes(
      state,
      transactionsSelectors.txnTypesDefault | transactionsSelectors.TxnTypes.REFUND_VIRTUAL_TXN,
    ),
  scheduledTransactionsSelectors.getScheduledTransactions,
  (state, monthsBack) => monthsBack,
  (state, monthsBack, monthsForward) => monthsForward,
  accountsSelectors.getAccountsById,
  (transactions, scheduledTxns, monthsBack, monthsForward, accountsById) => {
    let posted = new Map();
    let expected = new Map();
    const today = DateTime.local().startOf('day');
    const pastCutoff = today.minus({ months: monthsBack });
    const futureCutoff = today.plus({ months: monthsForward || 1 });
    transactions.forEach((txn) => {
      const txnDate =
        (txn.postedOn && DateTime.fromISO(txn.postedOn)) || (txn.stDueOn && DateTime.fromISO(txn.stDueOn));
      if (accountsById.get(txn.accountId) && !accountsById.get(txn.accountId).isClosed) {
        if ((txn.isBill || txn.isSubscription || txn.stModelId) && txn.subtype !== 'A2A_TRANSFER') {
          if (txnIsPosted(txn)) {
            if (txnDate && txnDate > pastCutoff) {
              posted.set(txn.id, txn);
            }
          } else if (txnDate && txnDate > pastCutoff && txnDate < futureCutoff) {
            // expected
            expected.set(txn.id, txn);
          }
        }
        if (txn.subtype === 'A2A_TRANSFER') {
          // Add A2A Transfers
          if (txn.state === 'CLEARED') {
            if (txnDate && txnDate > pastCutoff) {
              posted.set(txn.id, txn);
            }
          } else if (txnDate && txnDate > pastCutoff && txnDate < futureCutoff) {
            // expected
            expected.set(txn.id, txn);
          }
        }
        if (txn.source === 'USER_ENTERED' && txnDate && txnDate < futureCutoff && txnDate > today) {
          // Adding future dated manual transactions to the upcoming page
          expected.set(txn.id, txn);
        }
        // add refund tracking transactions
        const refundState = transactionsUtils.getRefundState(txn);
        switch (refundState) {
          case transactionsUtils.RefundState.EXPECTED:
          case transactionsUtils.RefundState.OVERDUE:
            if (txnDate > pastCutoff) {
              expected.set(txn.id, txn);
            }
            break;
          case transactionsUtils.RefundState.COMPLETE:
            if (txnDate > pastCutoff) {
              posted.set(txn.id, txn);
            }
            break;
          case transactionsUtils.RefundState.SHADOW:
          case transactionsUtils.RefundState.NONREFUND:
            break;
          default:
            assert(false, 'unexpected refund state: %s', refundState);
        }
      }
    });
    posted = OrderedMap(posted.entries()).sort((a, b) => new Date(b.postedOn) - new Date(a.postedOn));
    expected = OrderedMap(expected.entries()).sort(
      (a, b) => new Date(a.postedOn || a.stDueOn) - new Date(b.postedOn || b.stDueOn),
    );
    return {
      posted,
      expected,
    };
  },
);

export const getModelHistory = createSelector(
  transactionsSelectors.getTransactionsById,
  (state, model) => model,
  (transactions, model) => {
    const monthCutoff = DateTime.local().endOf('month');

    let oldestPending = { postedOn: '6942-01-01' };
    let pastModelTxns = ImmutableMap();
    if (model) {
      oldestPending = { ...model.transaction, postedOn: '6942-01-01' };
      pastModelTxns = transactions
        ? transactions.filter((txn) => {
            if (txn.stModelId === model.id && transferCheck(txn, model)) {
              const txnDate = transactionsUtils.dateTimeFromTxn(txn);
              if (
                txn.source === 'SCHEDULED_TRANSACTION_PENDING' &&
                txnDate < transactionsUtils.dateTimeFromTxn(oldestPending)
              ) {
                oldestPending = txn;
              }
              return txnDate < monthCutoff;
            }
            return false;
          })
        : ImmutableMap();
    }
    return { history: pastModelTxns.sort((a, b) => new Date(b.postedOn) - new Date(a.postedOn)), txn: oldestPending };
  },
);
